import { articlePath, articles } from './content/articles';
import type { Article } from './content/articleTypes';
import type { SiteRoute } from './router';
import { siteConfig, type SeoData } from './siteConfig';

const absolute = (path: string) => new URL(path, siteConfig.baseUrl).toString();
const organizationId = `${siteConfig.baseUrl}/#organization`;
const websiteId = `${siteConfig.baseUrl}/#website`;
const advisorId = `${siteConfig.baseUrl}/a-propos/#enzo-chevallier`;

const advisor = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': advisorId,
  name: siteConfig.advisor.name,
  jobTitle: siteConfig.advisor.role,
  url: absolute('/a-propos/'),
  worksFor: { '@id': organizationId },
};

const organization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': organizationId,
  name: siteConfig.name,
  url: `${siteConfig.baseUrl}/`,
  logo: absolute('/assets/brand/autosens-logo-horizontal-1200.png'),
  description:
    'Service indépendant d’aide au choix automobile pour les particuliers en France.',
  founder: { '@id': advisorId },
};

const breadcrumb = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absolute(item.path),
  })),
});

const articleSeo = (article: Article): SeoData => {
  const canonicalPath = articlePath(article.slug);
  return {
    title: article.seoTitle,
    description: article.description,
    canonicalPath,
    image: article.image,
    imageAlt: article.imageAlt,
    preloadImage: article.image,
    type: 'article',
    publishedAt: article.publishedAt,
    updatedAt: article.updatedAt,
    articleSection: article.category,
    structuredData: [
      organization,
      advisor,
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': `${absolute(canonicalPath)}#article`,
        headline: article.title,
        description: article.description,
        image: [absolute(article.image)],
        datePublished: article.publishedAt,
        dateModified: article.updatedAt,
        inLanguage: 'fr-FR',
        mainEntityOfPage: absolute(canonicalPath),
        author: { '@id': advisorId },
        publisher: { '@id': organizationId },
        isPartOf: { '@id': websiteId },
        articleSection: article.category,
      },
      breadcrumb([
        { name: 'Accueil', path: '/' },
        { name: 'Conseils', path: '/conseils/' },
        { name: article.title, path: canonicalPath },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };
};

export const getSeoForRoute = (route: SiteRoute): SeoData => {
  if (route.kind === 'article') return articleSeo(route.article);

  if (route.kind === 'guides') {
    return {
      title: 'Conseils pour choisir et acheter sa voiture | Auto Sens',
      description:
        'Guides indépendants pour choisir une voiture d’occasion, comparer les motorisations, comprendre le financement et vérifier un véhicule.',
      canonicalPath: '/conseils/',
      image: siteConfig.defaultImage,
      imageAlt: 'Voiture familiale argentée sur une vaste aire minérale',
      type: 'website',
      structuredData: [
        organization,
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': `${absolute('/conseils/')}#collection`,
          name: 'Conseils Auto Sens',
          description:
            'Guides indépendants pour choisir, financer et vérifier une voiture.',
          url: absolute('/conseils/'),
          inLanguage: 'fr-FR',
          isPartOf: { '@id': websiteId },
          hasPart: articles.map((article) => ({
            '@type': 'Article',
            name: article.title,
            url: absolute(articlePath(article.slug)),
          })),
        },
        breadcrumb([
          { name: 'Accueil', path: '/' },
          { name: 'Conseils', path: '/conseils/' },
        ]),
      ],
    };
  }

  if (route.kind === 'about') {
    return {
      title: 'Enzo Chevallier, conseiller automobile indépendant | Auto Sens',
      description:
        'Découvrez Enzo Chevallier, sa pratique personnelle de l’achat-revente automobile, sa méthode de comparaison et l’indépendance d’Auto Sens.',
      canonicalPath: '/a-propos/',
      image: siteConfig.defaultImage,
      imageAlt: 'Identité visuelle Auto Sens',
      type: 'website',
      structuredData: [
        organization,
        advisor,
        breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'À propos', path: '/a-propos/' }]),
      ],
    };
  }

  if (route.kind === 'tools') {
    return {
      title: 'Outils gratuits pour choisir une voiture | Auto Sens',
      description:
        'Diagnostic de projet, orientation de motorisation et comparateur de coût global : trois outils automobiles gratuits, sans inscription obligatoire.',
      canonicalPath: '/outils/',
      image: siteConfig.defaultImage,
      imageAlt: 'Identité visuelle Auto Sens',
      type: 'website',
      structuredData: [
        organization,
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Outils automobiles gratuits Auto Sens',
          url: absolute('/outils/'),
          inLanguage: 'fr-FR',
        },
        breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Outils', path: '/outils/' }]),
      ],
    };
  }

  if (route.kind === 'tool') {
    const tools = {
      'diagnostic-projet': [
        'Diagnostic de projet automobile gratuit | Auto Sens',
        'Évaluez en cinq questions le niveau de cadrage de votre recherche automobile et obtenez une prochaine étape concrète.',
      ],
      'orientation-motorisation': [
        'Essence, hybride, diesel ou électrique : outil gratuit',
        'Obtenez une orientation de motorisation à partir de vos kilomètres, trajets et possibilités de recharge.',
      ],
      'comparateur-cout-global': [
        'Comparateur de coût global automobile gratuit | Auto Sens',
        'Comparez deux voitures sur une même durée : achat, énergie, assurance, entretien et valeur de revente estimée.',
      ],
    } as const;
    const [title, description] = tools[route.tool];
    const canonicalPath = `/outils/${route.tool}/`;
    return {
      title,
      description,
      canonicalPath,
      image: siteConfig.defaultImage,
      imageAlt: 'Identité visuelle Auto Sens',
      type: 'website',
      structuredData: [
        organization,
        {
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: title.split(' | ')[0],
          description,
          url: absolute(canonicalPath),
          applicationCategory: 'UtilitiesApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
        },
        breadcrumb([
          { name: 'Accueil', path: '/' },
          { name: 'Outils', path: '/outils/' },
          { name: title.split(' | ')[0], path: canonicalPath },
        ]),
      ],
    };
  }

  if (route.kind === 'privacy') {
    return {
      title: 'Confidentialité et données personnelles | Auto Sens',
      description: 'Traitement des données du formulaire de contact et des outils gratuits Auto Sens.',
      canonicalPath: '/confidentialite/',
      image: siteConfig.defaultImage,
      imageAlt: 'Identité visuelle Auto Sens',
      type: 'website',
      robots: 'noindex, follow',
      structuredData: [organization],
    };
  }

  if (route.kind === 'unsubscribe') {
    return {
      title: 'Préférences e-mail | Auto Sens',
      description: 'Gérer les conseils de suivi Auto Sens.',
      canonicalPath: '/desinscription/',
      image: siteConfig.defaultImage,
      imageAlt: 'Identité visuelle Auto Sens',
      type: 'website',
      robots: 'noindex, nofollow',
      structuredData: [organization],
    };
  }

  if (route.kind === 'not-found') {
    return {
      title: 'Page introuvable | Auto Sens',
      description: 'La page demandée est introuvable.',
      canonicalPath: route.path,
      image: siteConfig.defaultImage,
      imageAlt: 'Voiture familiale argentée sur une vaste aire minérale',
      type: 'website',
      robots: 'noindex, nofollow',
      structuredData: [organization],
    };
  }

  return {
    title: 'Auto Sens, conseiller automobile indépendant à Angers et à distance',
    description:
      'Aide au choix d’une voiture d’occasion : shortlist de 3 à 5 modèles selon vos trajets, votre budget et le coût global. Sans commission, 249 € TTC, réponse sous 72 h.',
    canonicalPath: '/',
    image: siteConfig.defaultImage,
    imageAlt: 'Voiture familiale argentée sur une vaste aire minérale',
    preloadImage: '/assets/autosens-hero-classic.webp',
    type: 'website',
    structuredData: [
      organization,
      advisor,
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': websiteId,
        name: siteConfig.name,
        url: `${siteConfig.baseUrl}/`,
        inLanguage: 'fr-FR',
        publisher: { '@id': organizationId },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Conseil automobile indépendant pour particuliers',
        serviceType: 'Aide indépendante au choix automobile',
        provider: { '@id': organizationId },
        areaServed: [
          { '@type': 'City', name: 'Angers' },
          { '@type': 'Country', name: 'France' },
        ],
        availableChannel: [
          { '@type': 'ServiceChannel', name: 'Rendez-vous possible dans la région d’Angers' },
          { '@type': 'ServiceChannel', name: 'Téléphone ou visioconférence' },
        ],
        audience: { '@type': 'Audience', audienceType: 'Particuliers' },
        url: `${siteConfig.baseUrl}/`,
        description:
          'Shortlist de véhicules, comparaison du coût global et lecture des propositions de financement déjà reçues.',
        offers: {
          '@type': 'Offer',
          name: 'Offre pilote',
          price: '249',
          priceCurrency: 'EUR',
          url: `${siteConfig.baseUrl}/#contact`,
        },
      },
    ],
  };
};

export const absoluteSeoImage = (seo: SeoData) => absolute(seo.image);
